import React, { useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { useQuery, useQueryClient, useMutation } from "@tanstack/react-query";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Button } from "@/components/ui/button";
import { shopApi } from "@/lib/api";
import { useAuth, useCart } from "@/lib/store";
import { Package, Heart, Sparkles, X, Loader2, Check, Truck, Home, Copy, CreditCard, Clock } from "lucide-react";
import ProductCard from "@/components/ProductCard";
import ReferralCard from "@/components/ReferralCard";
import LoyaltyCard from "@/components/LoyaltyCard";
import { toast } from "sonner";

export default function Account() {
  const { user, loading } = useAuth();
  const nav = useNavigate();
  const [params] = useSearchParams();
  const tab = params.get("tab") || "orders";

  React.useEffect(() => { if (!loading && !user) nav("/login", { state: { from: "/account" }}); }, [user, loading, nav]);

  const { data: orders = [] } = useQuery({ queryKey: ["myOrders"], queryFn: shopApi.myOrders, enabled: !!user });
  const { data: wish = { items: [] } } = useQuery({ queryKey: ["wishlist"], queryFn: shopApi.wishlist, enabled: !!user });

  if (!user) return null;

  return (
    <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-10">
      <div className="mb-8">
        <p className="chip mb-2">Account</p>
        <h1 className="font-display text-3xl md:text-4xl font-bold tracking-tight">Hey, {user.name}</h1>
        <p className="text-muted-foreground text-sm">{user.email}</p>
      </div>
      <div className="mb-6"><LoyaltyCard /></div>
      <Tabs defaultValue={tab}>
        <TabsList className="bg-card border border-white/5">
          <TabsTrigger value="orders" data-testid="tab-orders"><Package className="h-4 w-4 mr-2" /> Orders</TabsTrigger>
          <TabsTrigger value="wishlist" data-testid="tab-wishlist"><Heart className="h-4 w-4 mr-2" /> Wishlist</TabsTrigger>
          <TabsTrigger value="referrals" data-testid="tab-referrals">Refer &amp; earn</TabsTrigger>
        </TabsList>
        <TabsContent value="orders" className="pt-6 space-y-4">
          {orders.length === 0 && (
            <div className="rounded-xl bg-card border border-white/5 p-8 text-center">
              <Package className="h-10 w-10 mx-auto text-muted-foreground mb-3" />
              <p className="font-medium mb-1">No orders yet</p>
              <p className="text-sm text-muted-foreground mb-4">Once you place an order, you'll be able to track it here.</p>
              <Button className="btn-primary" onClick={() => nav("/shop")} data-testid="empty-shop-btn">Start shopping</Button>
            </div>
          )}
          {orders.map((o) => <OrderCard key={o.order_id} order={o} />)}
        </TabsContent>
        <TabsContent value="wishlist" className="pt-6">
          <WishlistTab wish={wish} />
        </TabsContent>
        <TabsContent value="referrals" className="pt-6">
          <ReferralCard />
        </TabsContent>
      </Tabs>
    </div>
  );
}

function WishlistTab({ wish }) {
  const { add } = useCart();
  const nav = useNavigate();
  const [selected, setSelected] = useState({});
  const [suggested, setSuggested] = useState(null);
  const [loading, setLoading] = useState(false);
  const ids = Object.entries(selected).filter(([, v]) => v).map(([k]) => k);

  const toggle = (id) => setSelected((s) => ({ ...s, [id]: !s[id] }));

  const suggest = async () => {
    if (ids.length < 2) { toast.error("Pick at least 2 items"); return; }
    setLoading(true);
    try {
      const s = await shopApi.wishlistBundleSuggest(ids);
      setSuggested(s);
    } catch (e) { toast.error(e.response?.data?.detail || "Could not build a bundle"); }
    setLoading(false);
  };

  const addBundle = () => {
    for (const p of suggested.products) add(p, 1, null, suggested.bundle_id, false);
    toast.success("Bundle added to cart");
    setSuggested(null); setSelected({});
    nav("/checkout");
  };

  if (wish.items.length === 0) return <p className="text-muted-foreground">Your wishlist is empty.</p>;

  return (
    <div>
      <div className="rounded-2xl bg-gradient-to-r from-primary/10 via-card to-card border border-primary/20 p-4 mb-6 flex items-center justify-between gap-4 flex-wrap" data-testid="wishlist-bundle-cta">
        <div className="flex items-center gap-3">
          <Sparkles className="h-5 w-5 text-primary" />
          <div>
            <p className="text-sm font-semibold">Bundle savings unlocked</p>
            <p className="text-xs text-muted-foreground">Pick 2+ items from your wishlist to see a discounted bundle price.</p>
          </div>
        </div>
        <Button className="btn-primary" onClick={suggest} disabled={loading || ids.length < 2} data-testid="wishlist-suggest-btn">
          {loading ? <Loader2 className="h-4 w-4 animate-spin mr-1" /> : null} Suggest a bundle ({ids.length})
        </Button>
      </div>

      <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-6">
        {wish.items.map((p) => (
          <div key={p.product_id} className={`relative rounded-2xl overflow-hidden ${selected[p.product_id] ? "ring-2 ring-primary" : ""}`}>
            <label className="absolute top-3 left-3 z-10 flex items-center gap-2 rounded-full bg-black/80 backdrop-blur px-3 py-1.5 cursor-pointer" data-testid={`wl-select-${p.product_id}`}>
              <input type="checkbox" checked={!!selected[p.product_id]} onChange={() => toggle(p.product_id)} className="accent-primary" />
              <span className="text-xs text-white">Bundle</span>
            </label>
            <ProductCard product={p} />
          </div>
        ))}
      </div>

      {suggested && (
        <div className="fixed inset-x-0 bottom-0 z-40 border-t border-white/10 bg-card/95 backdrop-blur-lg" data-testid="wishlist-suggested-panel">
          <div className="max-w-6xl mx-auto p-4 flex items-center justify-between gap-4 flex-wrap">
            <div className="flex items-center gap-3">
              <div className="flex -space-x-3">
                {suggested.products.slice(0, 4).map((p) => (
                  <img key={p.product_id} src={p.images?.[0]} alt="" className="h-12 w-12 rounded-full border-2 border-card object-cover" />
                ))}
              </div>
              <div>
                <p className="text-sm font-semibold">Bundle of {suggested.products.length} items</p>
                <p className="text-xs text-muted-foreground">MSRP <span className="line-through">${suggested.msrp_total.toFixed(2)}</span> → <strong className="text-primary">${suggested.bundle_price.toFixed(2)}</strong> · save ${suggested.savings.toFixed(2)} ({Math.round(suggested.discount_percent)}%)</p>
              </div>
            </div>
            <div className="flex gap-2 ml-auto">
              <Button variant="outline" className="border-white/15" onClick={() => setSuggested(null)} data-testid="dismiss-suggestion"><X className="h-4 w-4" /></Button>
              <Button className="btn-primary" onClick={addBundle} data-testid="add-suggested-bundle">Add bundle & checkout</Button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}


// Amazon/Temu-style order card with visual status timeline.
// Shows: order id, date, ETA, items, per-step progress, tracking, total.
const ORDER_STEPS = [
  { key: "awaiting_payment", label: "Order placed", icon: Clock },
  { key: "paid",             label: "Payment confirmed", icon: CreditCard },
  { key: "processing",       label: "Preparing to ship", icon: Package },
  { key: "shipped",          label: "Shipped", icon: Truck },
  { key: "delivered",        label: "Delivered", icon: Home },
];

function OrderCard({ order }) {
  const o = order || {};
  const qc = useQueryClient();
  const [confirming, setConfirming] = React.useState(false);
  const cancelMut = useMutation({
    mutationFn: () => shopApi.cancelOrder(o.order_id),
    onSuccess: (r) => {
      toast.success("Order cancelled." + (r.refund_note ? " " + r.refund_note : ""));
      qc.invalidateQueries({ queryKey: ["orders"] });
    },
    onError: (e) => toast.error(e?.response?.data?.detail || "Could not cancel order"),
  });
  // Compute current step index from tracking_status (fall back to payment_status)
  const currentKey = (o.tracking_status || "").toLowerCase();
  const cancelled = currentKey === "cancelled";
  let idx = ORDER_STEPS.findIndex((s) => s.key === currentKey);
  if (idx === -1) idx = o.payment_status === "paid" ? 1 : 0;
  const canCancel = !cancelled && idx < 3; // block once "Shipped" or later
  const createdRaw = o.created_at || o.placed_at;
  const created = createdRaw ? new Date(createdRaw) : null;
  const eta = created ? new Date(created.getTime() + 7 * 864e5) : null; // ~7 day ETA
  const fmtDate = (d) => d ? d.toLocaleDateString(undefined, { month: "short", day: "numeric", year: "numeric" }) : "—";
  const copyTracking = () => {
    if (!o.tracking_number) return;
    navigator.clipboard.writeText(o.tracking_number);
    toast.success("Tracking number copied");
  };
  return (
    <div className="rounded-xl bg-card border border-white/5 overflow-hidden" data-testid={`order-row-${o.order_id}`}>
      {/* Header strip */}
      <div className="bg-secondary/30 px-4 py-3 flex flex-wrap items-start justify-between gap-3 border-b border-white/5">
        <div>
          <p className="text-[10px] uppercase tracking-widest text-muted-foreground">Order placed</p>
          <p className="text-sm font-medium">{fmtDate(created)}</p>
        </div>
        <div>
          <p className="text-[10px] uppercase tracking-widest text-muted-foreground">Total</p>
          <p className="text-sm font-bold text-primary">${o.totals?.total?.toFixed(2) ?? "0.00"}</p>
        </div>
        <div className="text-right">
          <p className="text-[10px] uppercase tracking-widest text-muted-foreground">Order #</p>
          <p className="text-xs font-mono">{(o.order_id || "").slice(-10)}</p>
        </div>
      </div>

      {/* Visual status timeline */}
      <div className="px-4 py-5">
        <div className="flex items-center justify-between mb-4">
          {ORDER_STEPS.map((s, i) => {
            const Icon = s.icon;
            const done = i < idx;
            const active = i === idx;
            return (
              <React.Fragment key={s.key}>
                <div className="flex flex-col items-center gap-1.5 w-14 shrink-0" data-testid={`step-${s.key}`}>
                  <div className={`h-8 w-8 rounded-full flex items-center justify-center shrink-0 transition ${
                    done ? "bg-primary text-primary-foreground" :
                    active ? "bg-primary/20 text-primary ring-2 ring-primary animate-pulse" :
                    "bg-secondary/40 text-muted-foreground"
                  }`}>
                    {done ? <Check className="h-4 w-4" /> : <Icon className="h-4 w-4" />}
                  </div>
                  <p className={`text-[9px] text-center leading-tight ${active ? "text-primary font-semibold" : done ? "text-white" : "text-muted-foreground"}`}>
                    {s.label}
                  </p>
                </div>
                {i < ORDER_STEPS.length - 1 && (
                  <div className={`h-0.5 flex-1 -mt-4 mx-1 ${i < idx ? "bg-primary" : "bg-white/10"}`} />
                )}
              </React.Fragment>
            );
          })}
        </div>
        {eta && idx < 4 && (
          <p className="text-xs text-muted-foreground text-center">
            <Truck className="inline h-3 w-3 mr-1" />
            Estimated delivery: <span className="text-white font-medium">{fmtDate(eta)}</span>
          </p>
        )}
        {idx === 4 && (
          <p className="text-xs text-emerald-400 text-center font-medium">
            ✓ Delivered on {fmtDate(new Date(o.updated_at || o.created_at))}
          </p>
        )}
      </div>

      {/* Items */}
      <div className="px-4 pb-4 border-t border-white/5 pt-4">
        <p className="text-[10px] uppercase tracking-widest text-muted-foreground mb-2">
          {(o.items || []).length} item{(o.items || []).length === 1 ? "" : "s"}
        </p>
        <div className="space-y-2">
          {(o.items || []).map((it, i) => (
            <div key={it.product_id || i} className="flex items-center gap-3">
              <img src={it.image} className="h-14 w-14 rounded-lg object-cover shrink-0" alt="" />
              <div className="flex-1 min-w-0">
                <p className="text-sm line-clamp-1">{it.title}</p>
                <p className="text-xs text-muted-foreground">Qty {it.quantity} · ${it.unit_price?.toFixed(2)}</p>
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* Tracking footer + Cancel button */}
      {cancelled ? (
        <div className="border-t border-white/5 px-4 py-4 bg-red-500/5 text-center">
          <p className="text-sm text-red-300 font-medium">Order cancelled</p>
          {o.payment_status === "refund_pending" && (
            <p className="text-xs text-muted-foreground mt-1">Refund will arrive within 3-5 business days.</p>
          )}
        </div>
      ) : (
        <div className="border-t border-white/5 px-4 py-3 flex items-center justify-between gap-3 bg-secondary/20">
          {o.tracking_number ? (
            <>
              <div className="min-w-0">
                <p className="text-[10px] uppercase tracking-widest text-muted-foreground">Tracking #</p>
                <p className="text-sm font-mono text-primary truncate">{o.tracking_number}</p>
              </div>
              <button
                onClick={copyTracking}
                className="text-xs px-3 py-1.5 rounded-full bg-primary/15 text-primary border border-primary/30 flex items-center gap-1.5 shrink-0"
                data-testid={`copy-tracking-${o.order_id}`}
              >
                <Copy className="h-3 w-3" /> Copy
              </button>
            </>
          ) : (
            <p className="text-xs text-muted-foreground">Tracking number will appear here soon</p>
          )}
          {canCancel && !confirming && (
            <button
              onClick={() => setConfirming(true)}
              className="text-xs px-3 py-1.5 rounded-full bg-red-500/10 text-red-300 border border-red-500/25 hover:bg-red-500/20 shrink-0"
              data-testid={`cancel-order-${o.order_id}`}
            >
              Cancel order
            </button>
          )}
        </div>
      )}
      {/* Cancel confirmation inline modal */}
      {confirming && !cancelled && (
        <div className="border-t border-red-500/25 px-4 py-4 bg-red-500/5">
          <p className="text-sm font-medium mb-1">Cancel this order?</p>
          <p className="text-xs text-muted-foreground mb-3">
            {o.payment_status === "paid"
              ? "Your payment will be refunded within 3-5 business days."
              : "This order hasn't been paid, so no refund is needed."}
          </p>
          <div className="flex gap-2">
            <button
              onClick={() => cancelMut.mutate()}
              disabled={cancelMut.isPending}
              className="flex-1 h-10 rounded-full bg-red-500 text-white font-semibold text-sm active:scale-95 transition disabled:opacity-60"
              data-testid={`confirm-cancel-${o.order_id}`}
            >
              {cancelMut.isPending ? "Cancelling..." : "Yes, cancel order"}
            </button>
            <button
              onClick={() => setConfirming(false)}
              disabled={cancelMut.isPending}
              className="flex-1 h-10 rounded-full bg-white/5 text-white text-sm border border-white/15"
            >
              Keep it
            </button>
          </div>
        </div>
      )}
    </div>
  );
}
